import {
  findLowestPriceProduct,
  Product,
  ProductIdentifiers,
  productsHaveSamePrice,
  ProductType
} from '@inter-ikea-storageone/range';
import { getProductById, getMarketProducts, getProductsByIds } from 'ducks/products';
import { RootState } from 'ducks';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import { getProductDescription, sortProductsByAscendingPrice } from 'products/utils';
import { useIntl } from 'react-intl';
import VariantsTable from './VariantsTable';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { isMobile } from 'utils/platform';
import styled from 'styled-components';
import HomeDelivery, { ReportCurrentPageEnum } from '../HomeDelivery/HomeDelivery';
import { IStoreAvailability, IZipAvailability } from '@inter-ikea-kompis/types/lib';
import StoreSelector from '../StoreSelector/StoreSelector';
import Sheet from 'ui/Sheet/Sheet';
import SheetHeader from 'ui/Sheet/SheetHeader';
import ImageSwiper from './ImageSwiper';
import Divider from 'ui/Divider/Divider';
import { getProductDetails, hideProductDetails } from 'ducks/layout';
import { useAvailability } from 'contexts/availability';
import { focusRestoreToLocalStorage } from 'utils/focusRestore';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import PriceModule from 'ui/PriceModule/PriceModule';
import Price from 'ui/Price/Price';
import { useAppDispatch } from 'ducks/hooks';
import { getQuery } from 'utils/queryUtils';
import ProductIdentifier from '@ingka/product-identifier';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import ComplianceInformation from './ComplianceInformation';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { KompisProductCardTippingHazardWarning } from '@inter-ikea-kompis/react-components';
import { IProduct } from '@inter-ikea-kompis/types';

const PriceWrapper = styled.div`
  padding: 0.25rem 0;
`;

const StyledPriceFormatter = styled(Price)`
  font-size: 25px;
`;

const combinationProducts = [
  ProductType.PAX_CORNER_SOLUTION_ASM,
  ProductType.PAX_INTERIOR_SUGGESTIONS,
  ProductType.PLATSA_INTERIOR_SUGGESTION,
  ProductType.SMASTAD_FRAME_NA_ASM,
  ProductType.IVAR_SECTION,
  ProductType.IVAR_CORNER_SECTION,
  ProductType.IVAR_INTERIOR_SUGGESTION,
  ProductType.SMASTAD_INTERIOR_SUGGESTION,
  ProductType.LADMAKARE_SECTION_WITH_SHELVES,
  ProductType.JONAXEL_INTERIOR_SUGGESTION
];

const hingeInformationProducts = [
  ProductType.PAX_HINGE_DOOR_ASM,
  ProductType.BESTA_DOOR_38_ASM,
  ProductType.BESTA_DOOR_64_ASM,
  ProductType.BESTA_DOOR_192_ASM,
  ProductType.PLATSA_HINGE_DOOR_ASM,
  ProductType.SMASTAD_HINGE_DOOR_ASM,
  ProductType.BILLY_DOOR_ASM,
  ProductType.IVAR_DOOR_ADJACENT,
  ProductType.IVAR_DOOR_STANDING
];

const ProductDetailsSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const productDetails = useSelector(getProductDetails);
  const marketProducts = useSelector(getMarketProducts);
  const generateAssembly =
    getQuery().get('generateInteriorAssembly') === 'true' ||
    getQuery().get('generateFrontAssembly') === 'true';

  const {
    storeId,
    stores,
    storeAvailabilities,
    zipAvailabilities,
    getHomeDeliveryForProducts,
    getStoreAvailabilityForProducts
  } = useAvailability();

  const [storeAvailability, setStoreAvailability] = useState<IStoreAvailability[] | undefined>(
    undefined
  );
  const [zipAvailability, setZipAvailability] = useState<IZipAvailability[] | undefined>(undefined);
  const [iProduct, setIProduct] = useState<IProduct>({
    reason: [],
    itemId: '',
    valid: false
  });

  const onClose = useCallback(() => {
    dispatch(hideProductDetails());
    focusRestoreToLocalStorage();
  }, [dispatch]);

  const productId = productDetails?.productId;

  const cluster = productDetails?.cluster
    ? productDetails?.cluster
        // Filter mirrored products as we don't want duplicates
        .filter(p => !p.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT))
        .map(p => p.coreProductId)
    : [];

  const getProductCluster = useSelector<RootState, Product[]>(state =>
    getProductsByIds(state, cluster)
  );

  const productCluster = sortProductsByAscendingPrice(getProductCluster);

  const product = useSelector<RootState, Product | undefined>(state =>
    productId ? getProductById(state, productId) : undefined
  );

  const nonComplimentaryProducts = product?.parts
    ?.filter(part => {
      return part.complimentary === false;
    })
    ?.map(part => {
      return marketProducts.find(p => p.globalItemNo === `${part.itemType}-${part.id}`);
    })
    .filter((p): p is Product => !!p) as Product[];

  useEffect(() => {
    if (product) {
      const storeAvailability = getStoreAvailabilityForProducts([product.globalItemNo]);
      const zipAvailability = getHomeDeliveryForProducts([product.globalItemNo]);
      const iProduct: IProduct = {
        reason: product?.reason,
        itemId: product?.globalItemNo,
        valid: product?.valid,
        content: product?.content
      };

      setStoreAvailability(storeAvailability);
      setZipAvailability(zipAvailability);
      setIProduct(iProduct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, storeAvailabilities, zipAvailabilities]);

  const isCombination = product && combinationProducts.includes(product.type);
  const hasMultipleSizes = !isCombination && productCluster.length > 1;

  const products = isCombination ? nonComplimentaryProducts : productCluster;
  const productsToShow = isCombination
    ? products
    : products.reduce((acc: Product[], product: Product) => {
        return acc.find(p => p.globalItemNo === product.globalItemNo) ? acc : [...acc, product];
      }, []);

  const productDetailsTitle =
    product?.type === ProductType.PAX_INTERIOR_SUGGESTIONS ||
    product?.type === ProductType.PLATSA_INTERIOR_SUGGESTION
      ? `${formatMessage({
          id: 'storageone.interiorSuggestionsCombinationsTabTitle'
        })}`
      : product?.name;

  const showAvailability = !hasMultipleSizes && !isCombination;
  const isFromPrice =
    productsToShow.length > 1 && !isCombination ? !productsHaveSamePrice(productsToShow) : false;

  const productDescriptions = getProductDescription(product, {
    typeName: !isCombination,
    designText: true,
    communicatedSizeText: !isCombination && !hasMultipleSizes
  });

  const lowestPriceProduct = findLowestPriceProduct(
    productCluster?.length > 0 ? productCluster : product ? [product] : []
  );
  const { shoppingProducts } = useFetchShoppingProducts(
    lowestPriceProduct ? [lowestPriceProduct] : []
  );

  return (
    <div data-body-scroll-lock-ignore="true">
      <Sheet
        data-testid={productId}
        data-product-id={product?.coreProductId}
        visible={!!product}
        onOverlayClick={onClose}
      >
        <SheetHeader data-testid="sheet-header" backIcon={false} onCloseButtonClick={onClose} />
        <Box overflowY="auto" height="100%" p={isMobile() ? 'l' : 'xxl'}>
          <ImageSwiper productDetails={productDetails} product={product} />
          <Title mt="md" type={ThemeFontStyleTypeEnum.headingXSmall}>
            {productDetailsTitle}
          </Title>
          <Body color="grey.500" type={ThemeFontStyleTypeEnum.bodyMedium}>
            {productDescriptions}
          </Body>
          {!generateAssembly && shoppingProducts?.length > 0 && (
            <PriceWrapper>
              {lowestPriceProduct?.priceInformation?.currentPrice ? (
                <>
                  <PriceModule
                    shoppingProducts={shoppingProducts}
                    isFromPrice={isFromPrice}
                    isCombinedProduct={product && product.parts.length > 1}
                  />
                  {!hasMultipleSizes && product && product.ruItemNo && !isCombination && (
                    <ProductIdentifier value={ProductUtility.formatItemNo(product.ruItemNo)} />
                  )}
                  {product && iProduct.content?.complianceInformation !== null && (
                    <Box mt="l">
                      <KompisProductCardTippingHazardWarning product={iProduct} />
                    </Box>
                  )}
                </>
              ) : (
                <Body type={ThemeFontStyleTypeEnum.bodyMedium} fontWeight="bold">
                  <StyledPriceFormatter amount={0} />
                </Body>
              )}
              {product && !(hasMultipleSizes || isCombination) && (
                <ComplianceInformation product={product} />
              )}
            </PriceWrapper>
          )}
          {product && hingeInformationProducts.includes(product?.type) && (
            <Box mt="12px">
              <SkapaInlineMessage
                body={formatMessage({ id: 'storageone.additionalHingesInfo' })}
                variant={InlineMessageVariantEnum.INFORMATIVE}
                ssrIcon={InformationCircle}
                subtle
              />
            </Box>
          )}
          {hasMultipleSizes || isCombination ? (
            <VariantsTable products={productsToShow} isCombination={!!isCombination} />
          ) : (
            <Divider mt="md" />
          )}
          <HomeDelivery
            currentPage={ReportCurrentPageEnum.productInfo}
            zipAvailability={showAvailability ? zipAvailability : undefined}
            mt="l"
          />
          {stores.length > 0 && (
            <StoreSelector
              mt="md"
              storeId={storeId}
              stores={stores}
              storeAvailabilities={showAvailability ? storeAvailability : undefined}
              openExternalStoreSelectorSheet
              currentPage={ReportCurrentPageEnum.productInfo}
              id={'product-details-store-selector-btn-' + productId}
            />
          )}
        </Box>
      </Sheet>
    </div>
  );
};

export default ProductDetailsSheet;
